import React, { useState, useEffect, useRef } from "react";
import {
  Row,
  Col,
  Card,
  Form,
  Input,
  Button,
} from "antd";
import { EditOutlined, SaveOutlined } from "@ant-design/icons";
import useRequest from "../hooks/useRequest";
import { ShowToast, Severty } from "../helper/toast";

const PricingManagement = () => {
  const [form] = Form.useForm();
  const { request } = useRequest();
  const [loading, setLoading] = useState(false);
  const [isEditingReport, setIsEditingReport] = useState(false);
  const [isEditingCertificate, setIsEditingCertificate] = useState(false);
  const reportInputRef = useRef(null);
  const certificateInputRef = useRef(null);

  useEffect(() => {
    fetchPricing();
  }, []);

  const fetchPricing = () => {
    setLoading(true);
    request({
      url: "/admin/certification/getPricing",
      method: "GET",
      onSuccess: (data) => {
        form.setFieldsValue({
          reportPrice: data?.data?.reportPrice || 0,
          certificatePrice: data?.data?.certificatePrice || 0,
        });
        setLoading(false);
      },
      onError: (error) => {
        setLoading(false);
        ShowToast(error?.response?.data?.message || "Failed to fetch pricing", Severty.ERROR);
      },
    });
  };

  const updateCredit = (field) => {
    const values = form.getFieldsValue();
    const payload = {
      creditRanges: {
        report: values.reportPrice,
        certificate: values.certificatePrice,
      },
    };

    request({
      url: "/admin/certification/creditRange",
      method: "POST",
      data: payload,
      onSuccess: () => {
        ShowToast("Pricing updated successfully", Severty.SUCCESS);
        if (field === "report") {
          setIsEditingReport(false);
        } else if (field === "certificate") {
          setIsEditingCertificate(false);
        }
      },
      onError: (err) => {
        ShowToast(err?.response?.data?.message || "Something went wrong", Severty.ERROR);
      },
    });
  };

  useEffect(() => {
    if (isEditingReport) {
      reportInputRef.current?.focus();
    }
  }, [isEditingReport]);

  useEffect(() => {
    if (isEditingCertificate) {
      certificateInputRef.current?.focus();
    }
  }, [isEditingCertificate]);

  return (
    <Card className="profile-nav" style={{ flex: 1 }}>
      <div className="profile-nav-inner">
        <Form
          form={form}
          layout="vertical"
          className="change-pw main-new-commition-text"
          style={{ width: "100%" }}
        >
          <h4>Pricing Management</h4>
          <Row gutter={[24, 4]}>
            <Col span={12}>
              <Form.Item
                label="Report Credit $"
                name="reportPrice"
                rules={[
                  {
                    required: true,
                    message: "Report price is required",
                  },
                  {
                    validator: (_, value) => {
                      if (value < 0 || isNaN(value) || value.toString().includes('-')) {
                        return Promise.reject("Price must be a positive number");
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input
                  ref={reportInputRef}
                  type="number"
                  placeholder="Amount in USD"
                  className="input-field-edit"
                  readOnly={!isEditingReport}
                  min={1}
                  onKeyPress={(e) => {
                    if (!/[0-9]/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  suffix={
                    isEditingReport ? (
                      <Button
                        type="primary"
                        icon={<SaveOutlined />}
                        onClick={() => updateCredit("report")}
                      />
                    ) : (
                      <EditOutlined
                        style={{ cursor: "pointer", color: "#1890ff" }}
                        onClick={() => setIsEditingReport(true)}
                      />
                    )
                  }
                />
              </Form.Item>

              <Form.Item
                label="Certificate Credit $"
                name="certificatePrice"
                rules={[
                  {
                    required: true,
                    message: "Certificate price is required",
                  },
                  {
                    validator: (_, value) => {
                      if (value < 0 || isNaN(value) || value.toString().includes('-')) {
                        return Promise.reject("Price must be a positive number");
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input
                  ref={certificateInputRef}
                  type="number"
                  placeholder="Amount in USD"
                  className="input-field-edit"
                  readOnly={!isEditingCertificate}
                  min={1}
                  onKeyPress={(e) => {
                    if (!/[0-9]/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  suffix={
                    isEditingCertificate ? (
                      <Button
                        type="primary"
                        icon={<SaveOutlined />}
                        onClick={() => updateCredit("certificate")}
                      />
                    ) : (
                      <EditOutlined
                        style={{ cursor: "pointer", color: "#1890ff" }}
                        onClick={() => setIsEditingCertificate(true)}
                      />
                    )
                  }
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </div>
    </Card>
  );
};

export default PricingManagement;
